import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  IconButton,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  AccordionDetailsStyled,
  AccordionStyled,
  AccordionSummaryStyled,
} from "app/pages/components/mui/Accordions/StyledAccordion/StyleAccordion";
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from "react-sortable-hoc";
import CustomDragHandle from "components/CustomDragIcon";

// Sortable video item
const SortableVideoItem = SortableElement(
  ({
    video,
    vIndex,
    expandedVideoIndex,
    handleVideoAccordionChange,
    onVideoChange,
    onDeleteVideo,
    onDuplicateVideo,
    videoProviders,
    programmingLanguages,
    visibility,
  }) => {
    if (vIndex === undefined) {
      console.error("SortableVideoItem received undefined vIndex", { video });
    }
    return (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          mb: 1,
          visibility: "visible !important",
          opacity: "1 !important",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 1,
            px: 2,
            cursor: "pointer",
            backgroundColor:
              expandedVideoIndex === vIndex ? "#f5f5f5" : "inherit",
          }}
          onClick={() => handleVideoAccordionChange(vIndex)}
        >
          <CustomDragHandle />
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {video.videoTitle || `Video ${vIndex + 1}`}
          </Typography>
          <IconButton
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              if (vIndex === undefined) {
                console.error("Cannot delete: vIndex is undefined");
                return;
              }
              onDeleteVideo(vIndex);
            }}
          >
            <DeleteIcon />
          </IconButton>
          <ExpandMoreIcon
            sx={{
              transform:
                expandedVideoIndex === vIndex
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </Box>
        {expandedVideoIndex === vIndex && (
          <AccordionDetailsStyled
            sx={{ padding: "16px", borderTop: "1px solid #e0e0e0" }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              <TextField
                label="Video Link"
                placeholder="Enter video link"
                value={video.videoLink || ""}
                onChange={(e) => {
                  onVideoChange(vIndex, "videoLink", e.target.value);
                }}
                required
                sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
              />
              <TextField
                label="Video Title"
                placeholder="Enter video title"
                value={video.videoTitle || ""}
                onChange={(e) => {
                  onVideoChange(vIndex, "videoTitle", e.target.value);
                }}
                required
                sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
              />
              <FormControl sx={{ minWidth: "200px" }} required>
                <InputLabel id={`provider-${vIndex}`}>Provider</InputLabel>
                <Select
                  labelId={`provider-${vIndex}`}
                  label="Provider"
                  value={video.provider || ""}
                  onChange={(e) => {
                    onVideoChange(vIndex, "provider", e.target.value);
                  }}
                  displayEmpty
                  renderValue={(value) =>
                    value
                      ? videoProviders.find((p) => p.key === value)?.value ||
                        value
                      : "Select Provider"
                  }
                >
                  {videoProviders.map((provider) => (
                    <MenuItem key={provider.key} value={provider.key}>
                      {provider.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: "200px" }} required>
                <InputLabel id={`programmingLanguage-${vIndex}`}>
                  Programming Language
                </InputLabel>
                <Select
                  labelId={`programmingLanguage-${vIndex}`}
                  label="Programming Language"
                  value={video.programmingLanguage || ""}
                  onChange={(e) => {
                    onVideoChange(
                      vIndex,
                      "programmingLanguage",
                      e.target.value
                    );
                  }}
                  displayEmpty
                  renderValue={(value) =>
                    value
                      ? programmingLanguages.find((p) => p.key === value)
                          ?.value || value
                      : "Select Programming Language"
                  }
                >
                  {programmingLanguages?.map((language) => (
                    <MenuItem key={language.key} value={language.key}>
                      {language.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: "200px" }} required>
                <InputLabel id={`visibility-${vIndex}`}>Visibility</InputLabel>
                <Select
                  labelId={`visibility-${vIndex}`}
                  label="Visibility"
                  value={video.visibility || ""}
                  onChange={(e) => {
                    onVideoChange(vIndex, "visibility", e.target.value);
                  }}
                  displayEmpty
                  renderValue={(value) =>
                    value
                      ? visibility.find((p) => p.key === value)?.value || value
                      : "Select Visibility"
                  }
                >
                  {visibility?.map((v) => (
                    <MenuItem key={v.key} value={v.key}>
                      {v.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Video Description"
                placeholder="Enter video description"
                value={video.videoDescription || ""}
                onChange={(e) => {
                  onVideoChange(vIndex, "videoDescription", e.target.value);
                }}
                multiline
                rows={3}
                sx={{ gridColumn: "1 / -1" }}
              />
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={video.active || false}
                        onChange={(e) => {
                          onVideoChange(vIndex, "active", e.target.checked);
                        }}
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={video.previewAvailable || false}
                        onChange={(e) => {
                          onVideoChange(
                            vIndex,
                            "previewAvailable",
                            e.target.checked
                          );
                        }}
                      />
                    }
                    label="Preview Available"
                  />
                </Box>
                <Tooltip title="Duplicate" arrow placement="top">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (vIndex === undefined) {
                        console.error("Cannot duplicate: vIndex is undefined");
                        return;
                      }
                      onDuplicateVideo(vIndex);
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </AccordionDetailsStyled>
        )}
      </Box>
    );
  }
);

// Sortable container for videos
const SortableVideosList = SortableContainer(
  ({
    problemVideos,
    expandedVideoIndex,
    handleVideoAccordionChange,
    onVideoChange,
    onDeleteVideo,
    onDuplicateVideo,
    videoProviders,
    programmingLanguages,
    visibility,
  }) => {
    if (!Array.isArray(problemVideos)) {
      console.error("problemVideos is not an array:", problemVideos);
      return (
        <AccordionDetailsStyled>No videos available</AccordionDetailsStyled>
      );
    }
    return (
      <AccordionDetailsStyled>
        {problemVideos.length === 0 ? (
          <Typography>No videos available</Typography>
        ) : (
          problemVideos.map((video, vIndex) => {
            return (
              <SortableVideoItem
                key={video.id || `video-${vIndex}`}
                index={vIndex}
                vIndex={vIndex}
                collection={0}
                video={video}
                expandedVideoIndex={expandedVideoIndex}
                handleVideoAccordionChange={handleVideoAccordionChange}
                onVideoChange={onVideoChange}
                onDeleteVideo={onDeleteVideo}
                onDuplicateVideo={onDuplicateVideo}
                videoProviders={videoProviders}
                programmingLanguages={programmingLanguages}
                visibility={visibility}
              />
            );
          })
        )}
      </AccordionDetailsStyled>
    );
  }
);

const VideoListAccordion = ({
  title = "Videos",
  problemVideos,
  onAddVideo,
  onDeleteVideo,
  onVideoChange,
  expanded,
  onAccordionChange,
  videoProviders,
  programmingLanguages,
  visibility,
}) => {
  const [expandedVideoIndex, setExpandedVideoIndex] = useState(null);
  const prevVideosRef = useRef(problemVideos);

  // Sync state changes
  useEffect(() => {
    prevVideosRef.current = problemVideos;
  }, [problemVideos]);

  const handleVideoAccordionChange = (index) => {
    setExpandedVideoIndex(index);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const reorderedVideos = arrayMove(problemVideos, oldIndex, newIndex);
    onVideoChange(-1, "reorder", reorderedVideos);
  };

  const onDuplicateVideo = (index) => {
    if (index === undefined) {
      console.error("Cannot duplicate video: index is undefined");
      return;
    }
    const videoToDuplicate = problemVideos[index];
    const newVideo = {
      ...videoToDuplicate,
      id: `${Date.now()}-${problemVideos.length}`,
      videoTitle: `${videoToDuplicate.videoTitle} (Copy)`,
    };
    const updatedVideos = [
      ...problemVideos.slice(0, index + 1),
      newVideo,
      ...problemVideos.slice(index + 1),
    ];
    onVideoChange(-1, "duplicate", updatedVideos);
  };

  return (
    <AccordionStyled
      expanded={expanded}
      onChange={onAccordionChange}
      sx={{ width: "100%" }}
    >
      <AccordionSummaryStyled
        expandIcon={<ExpandMoreIcon />}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography variant="subtitle1">{title}</Typography>
          {expanded && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onAddVideo();
              }}
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummaryStyled>
      <SortableVideosList
        problemVideos={problemVideos}
        expandedVideoIndex={expandedVideoIndex}
        handleVideoAccordionChange={handleVideoAccordionChange}
        onVideoChange={onVideoChange}
        onDeleteVideo={onDeleteVideo}
        onDuplicateVideo={onDuplicateVideo}
        videoProviders={videoProviders}
        programmingLanguages={programmingLanguages}
        visibility={visibility}
        onSortEnd={onSortEnd}
        useDragHandle={true}
        axis="y"
        lockAxis="y"
        collection={0}
      />
    </AccordionStyled>
  );
};

export default VideoListAccordion;
